/**
 * MinistrareIA - WhatsApp Business Chat Window Component
 * Padronizado com WhatsApp Pessoal + AI Workspace
 * 
 * Diferenças de estrutura de dados (mantidas internamente):
 * - chat.id instead of chat.remoteJid
 * - msg.wamid instead of msg.key.id
 * - msg.from_me instead of msg.key?.fromMe
 * - msg.text instead of msg.message?.conversation
 * - msg.media_url, msg.media_type for media
 * - msg.timestamp (ISO string) instead of messageTimestamp
 * - Handles 24h window error (específico do Business)
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Send,
  Paperclip,
  MoreVertical,
  User,
  Loader2,
  ArrowDown,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  X,
  Bot,
  ShieldOff,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
import waBusinessService from '../../services/wa-business/waBusinessService';
import WABusinessMessageMedia from '../../components/WABusinessMedia';

// AI Workspace imports
import { 
  AIWorkspace, 
  MessageCheckbox, 
  AttachedFilesPreview,
  useAIWorkspace,
  getWorkspaceColors,
  BLOCKED_MESSAGE_TYPES,
  MAX_SELECTED_MESSAGES,
  ACTION_TYPES,
} from '../../components/AIWorkspace';
import aiAgentService from '../../services/global/aiAgentService';
import fileUploadService from '../../services/global/fileUploadService';
import useAgentCheck from '../../components/AIWorkspace/hooks/useAgentCheck';

// ============================================
// UTILITY FUNCTIONS (outside component for reuse)
// ============================================

/**
 * Robustly parse timestamp from various formats
 * Handles: ISO strings, Unix timestamps (seconds/milliseconds), Date objects
 */
const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // Already a valid Date object
  if (timestamp instanceof Date) {
    return isNaN(timestamp.getTime()) ? null : timestamp;
  }
  
  // Number: Unix timestamp (seconds or milliseconds)
  if (typeof timestamp === 'number') {
    // If timestamp is in seconds (< year 3000 in seconds), convert to ms
    // Unix timestamp in seconds: 1706097600 (10 digits)
    // Unix timestamp in ms: 1706097600000 (13 digits)
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // String: try multiple parsing strategies
  if (typeof timestamp === 'string') {
    // Try direct ISO parse first
    let date = new Date(timestamp);
    if (!isNaN(date.getTime())) return date;
    
    // Try parsing as numeric string (Unix timestamp)
    const numericValue = Number(timestamp);
    if (!isNaN(numericValue)) {
      const ms = numericValue < 10000000000 ? numericValue * 1000 : numericValue;
      date = new Date(ms);
      if (!isNaN(date.getTime())) return date;
    }
    
    // Try common date formats that might fail in some browsers
    // Format: "2024-01-24 12:00:00" (without T)
    const withT = timestamp.replace(' ', 'T');
    date = new Date(withT);
    if (!isNaN(date.getTime())) return date;
  }
  
  return null;
};

/**
 * Get timestamp value for sorting (returns ms or 0)
 */
const getTimestampMs = (timestamp) => {
  const date = parseTimestamp(timestamp);
  return date ? date.getTime() : 0;
};

const ChatWindow = ({ chat, onMessageSent, autopilotContacts, onToggleAutopilot, blockedContacts, onToggleBlockedContact }) => {
  const { colors, theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [windowError, setWindowError] = useState(null); // 24h window error
  const [regeneratingId, setRegeneratingId] = useState(null);
  
  // Attached files state (from AI Workspace)
  const [attachedFiles, setAttachedFiles] = useState([]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const currentChatRef = useRef(null);
  const fileInputRef = useRef(null);

  // AI Workspace hook
  const aiWorkspace = useAIWorkspace();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  
  // File upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Keep track of current chat for SSE callbacks
  useEffect(() => {
    currentChatRef.current = chat;
  }, [chat]);

  // Reset workspace and attached files when chat changes
  useEffect(() => {
    aiWorkspace.resetWorkspace();
    setNewMessage('');
    setAttachedFiles([]);
    setWindowError(null);
  }, [chat?.id]);

  const { 
    handleAgentError, 
    CreateAgentModal,
    isAgentConfigured 
  } = useAgentCheck();

  // ============================================
  // SSE EVENT HANDLERS
  // ============================================

  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;

    // Only process WA Business messages
    if (data.source !== 'wa_business') return;

    // Check if message is for current chat
    if (data.chat_id !== currentChat.id) return;

    console.log('[WA Business ChatWindow] SSE NEW_MESSAGE:', data);

    const newMsg = data.message;
    if (!newMsg) return;

    setMessages(prev => {
      const exists = prev.some(m => 
        m.id === newMsg.id || 
        m.wamid === newMsg.wamid
      );
      
      if (exists) return prev;
      
      const updated = [...prev, newMsg];
      setTimeout(() => scrollToBottom(), 100);
      return updated;
    });

    // Mark as read
    if (!newMsg.from_me) {
      waBusinessService.markAsRead(currentChat.id).catch(() => {});
    }
  }, []);

  useSSEEvent(SSE_EVENTS.MESSAGE_STATUS, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;

    if (data.source !== 'wa_business') return;

    console.log('[WA Business ChatWindow] SSE MESSAGE_STATUS:', data);

    setMessages(prev => prev.map(msg => {
      if (msg.wamid === data.wamid) {
        return { 
          ...msg, 
          status: data.status,
          error_message: data.error || null 
        };
      }
      return msg;
    }));

    // Show error if 24h window
    if (data.error && data.error.includes('24h')) {
      setWindowError(data.error);
    }
  }, []);

  // ============================================
  // LOAD MESSAGES
  // ============================================

  const loadMessages = useCallback(async () => {
    if (!chat?.id) return;

    setLoading(true);
    setWindowError(null);

    try {
      const response = await waBusinessService.getMessages(chat.id, 100);
      let loadedMessages = response.data || [];
      
      // Sort by timestamp (oldest first) using robust parsing
      loadedMessages = [...loadedMessages].sort((a, b) => {
        const timeA = getTimestampMs(a.timestamp) || getTimestampMs(a.created_at);
        const timeB = getTimestampMs(b.timestamp) || getTimestampMs(b.created_at);
        return timeA - timeB;
      });

      setMessages(loadedMessages);

      // Mark as read
      waBusinessService.markAsRead(chat.id).catch(() => {});

      setTimeout(() => scrollToBottom(), 100);

    } catch (err) {
      console.error('[WA Business ChatWindow] Error loading messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [chat?.id]);

  useEffect(() => {
    if (chat?.id) {
      loadMessages();
    } else {
      setMessages([]);
    }
  }, [chat?.id, loadMessages]);

  useEffect(() => {
    if (chat && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chat]);

  // ============================================
  // HELPERS
  // ============================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
  };

  // ============================================
  // SEND MESSAGE (with files support)
  // ============================================

  const handleSend = async (e, textOverride = null) => {
    e?.preventDefault();
    const messageText = textOverride || newMessage.trim();
    
    const hasText = messageText && messageText.length > 0;
    const hasFiles = attachedFiles.length > 0;
    
    if (!hasText && !hasFiles) return;
    if (sending) return;

    const filesToSend = [...attachedFiles];
    
    // Clear input immediately
    if (!textOverride) {
      setNewMessage('');
    }
    setAttachedFiles([]);
    setWindowError(null);
    
    setSending(true);
    setTimeout(() => setSending(false), 300);

    // Create temp message for optimistic UI
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      wamid: tempId,
      from_me: true,
      from_phone: '',
      to_phone: chat.contact_phone,
      type: hasFiles ? (filesToSend[0]?.type || 'document') : 'text',
      text: messageText || null,
      media_url: hasFiles ? filesToSend[0]?.url : null,
      status: 'pending',
      timestamp: new Date().toISOString(),
      _isTemp: true,
      _attachedFiles: filesToSend,
    };
    
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const phone = chat.contact_phone;
      
      console.log('[WA Business ChatWindow] Sending:', {
        phone,
        textLength: messageText?.length || 0,
        filesCount: filesToSend.length,
      });
      
      let result;
      
      if (hasFiles) {
        result = await waBusinessService.sendMessageWithMedia(
          phone,
          messageText || null,
          filesToSend.map(f => ({
            url: f.url,
            type: f.type || 'document',
            filename: f.filename,
            caption: null,
          }))
        );
        
        if (result.partial) {
          console.warn('[WA Business ChatWindow] Partial success:', result.results);
        }
        
        const realId = result.results?.find(r => r.data?.message_id)?.data?.message_id;
        
        if (realId) {
          setMessages(prev => prev.map(m => 
            m.id === tempId 
              ? { ...m, id: realId, wamid: realId, status: 'sent', _isTemp: false }
              : m
          ));
        }
      } else {
        result = await waBusinessService.sendMessage(chat.id, messageText);
        
        if (result.success) {
          const realId = result.message_id;
          if (realId) {
            setMessages(prev => prev.map(m => 
              m.id === tempId 
                ? { ...m, id: realId, wamid: realId, status: 'sent', _isTemp: false }
                : m
            ));
          }
        } else {
          throw new Error(result.error || 'Falha ao enviar');
        }
      }
      
      onMessageSent?.({ text: messageText, files: filesToSend });
      
    } catch (err) {
      console.error('[WA Business ChatWindow] Send error:', err);
      
      const errorMsg = err.response?.data?.detail || err.message;
      
      // Check for 24h window error
      if (errorMsg && (errorMsg.includes('24h') || errorMsg.includes('janela'))) {
        setWindowError('Fora da janela de 24h. O contato precisa enviar uma mensagem primeiro.');
        setMessages(prev => prev.filter(m => m.id !== tempId));
      } else {
        setMessages(prev => prev.map(m => 
          m.id === tempId 
            ? { ...m, status: 'failed', error_message: errorMsg }
            : m
        ));
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  };

  // ============================================
  // MESSAGE HELPERS
  // ============================================

  const formatTime = (timestamp) => {
    const date = parseTimestamp(timestamp);
    if (!date) return '';
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = parseTimestamp(timestamp);
    if (!date) return 'Data desconhecida';
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Hoje';
    if (date.toDateString() === yesterday.toDateString()) return 'Ontem';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const getMessageStatusIcon = (status) => {
    const iconStyle = { marginLeft: 4 };
    
    switch (status) {
      case 'read':
        return <CheckCheck size={14} style={{ ...iconStyle, color: '#53bdeb' }} />;
      case 'delivered':
        return <CheckCheck size={14} style={{ ...iconStyle, color: colors.messageTimeSent || 'rgba(255,255,255,0.6)' }} />;
      case 'sent':
        return <Check size={14} style={{ ...iconStyle, color: colors.messageTimeSent || 'rgba(255,255,255,0.6)' }} />;
      case 'pending':
        return <Loader2 size={12} style={{ ...iconStyle, color: colors.messageTimeSent || 'rgba(255,255,255,0.6)', animation: 'spin 1s linear infinite' }} />;
      case 'failed':
        return <AlertCircle size={14} style={{ ...iconStyle, color: colors.danger || '#ef4444' }} />;
      default:
        return null;
    }
  };

  const getMessageText = (msg) => {
    return msg.text || '';
  };

  const isMessageBlocked = (msg) => {
    // For WA Business, only text messages can be selected
    return msg.type !== 'text' || !msg.text;
  };

  const hasMediaContent = (msg) => {
    return msg.media_url && msg.type !== 'text';
  };

  const getChatName = () => {
    return chat?.contact_name || chat?.contact_phone || 'Desconhecido';
  };

  const getChatAvatar = () => {
    if (chat?.contact_profile_picture_url) {
      return (
        <img 
          src={chat.contact_profile_picture_url} 
          alt="" 
          style={styles.avatarImg}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }
    
    return (
      <div style={styles.avatarPlaceholder}>
        <User size={24} />
      </div>
    );
  };

  // ============================================
  // ATTACHED FILES HANDLERS
  // ============================================

  const handleRemoveAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAttachedFiles = () => {
    setAttachedFiles([]);
  };

  // ============================================
  // FILE UPLOAD HANDLERS
  // ============================================

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);
    e.target.value = '';

    setUploading(true);
    setUploadError(null);

    try {
      console.log('[WA Business ChatWindow] Uploading files:', files.length);

      const results = await fileUploadService.uploadMultiple(files, (progress) => {
        console.log('[WA Business ChatWindow] Upload progress:', progress);
      });

      if (results.uploaded.length > 0) {
        setAttachedFiles(prev => [...prev, ...results.uploaded]);
        console.log('[WA Business ChatWindow] Files uploaded:', results.uploaded.length);
      }

      if (results.errors.length > 0) {
        const errorMsg = results.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
        setUploadError(errorMsg);
        console.error('[WA Business ChatWindow] Upload errors:', results.errors);
        setTimeout(() => setUploadError(null), 5000);
      }

    } catch (error) {
      console.error('[WA Business ChatWindow] Upload failed:', error);
      setUploadError(error.message);
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // AI WORKSPACE HANDLERS
  // ============================================

  const handleExecuteTranslate = async () => {
  if (!newMessage.trim()) return;

  const originalText = newMessage.trim();

  try {
    await aiWorkspace.executeTranslate(originalText, async ({ action, text, languages }) => {
      const results = await aiAgentService.processAction({
        action,
        text,
        languages,
      });
      
      return results.map(r => ({
        ...r,
        originalText: originalText,
      }));
    });
  } catch (error) {
    // 🆕 ADICIONAR: Verificar se é erro de agente não configurado
    if (handleAgentError(error)) return;
    
    // Se não for erro de agente, log normal
    console.error('[ChatWindow] Translate error:', error);
  }
};

  const handleExecuteGenerate = async () => {
  const selectedMsgs = messages.filter(msg => {
    const msgId = msg.id || msg.wamid;
    return aiWorkspace.selectedMessages.has(msgId);
  });
  
  const messageTexts = selectedMsgs.map(getMessageText).filter(Boolean);
  
  if (messageTexts.length === 0) return;

  const contextWithMessages = {
    chatId: chat?.id,
    chatName: getChatName(),
    originalMessages: messageTexts,
  };

  try {
    await aiWorkspace.executeGenerate(messageTexts, async ({ action, messages: msgs }) => {
      const results = await aiAgentService.processAction({
        action,
        messages: msgs,
        languages: [],
        context: contextWithMessages,
      });
      
      return results.map(r => ({
        ...r,
        originalMessages: messageTexts,
      }));
    }, contextWithMessages);
  } catch (error) {
    // 🆕 ADICIONAR: Verificar se é erro de agente não configurado
    if (handleAgentError(error)) return;
    
    console.error('[ChatWindow] Generate error:', error);
  }
};


  const handleUseResponse = (result) => {
    setNewMessage(result.text);
    
    if (result.files && result.files.length > 0) {
      setAttachedFiles(prev => [...prev, ...result.files]);
      console.log('[WA Business ChatWindow] Attached files from AI response:', result.files.length);
    }
    
    inputRef.current?.focus();
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 100) + 'px';
      }
    }, 0);
  };

  const handleRegenerateTranslate = async (result) => {
  if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.TRANSLATE) <= 0) return;
  
  setRegeneratingId(result.id);
  
  try {
    const originalText = result.originalText || result.text;
    
    await aiWorkspace.regenerateResult(
      result.id, 
      ACTION_TYPES.TRANSLATE,
      async (params) => {
        const results = await aiAgentService.processAction({
          ...params,
          text: originalText,
        });
        
        return results.map(r => ({
          ...r,
          originalText: originalText,
        }));
      },
      { text: originalText, originalText }
    );
  } catch (error) {
    // 🆕 ADICIONAR
    if (handleAgentError(error)) return;
    console.error('[ChatWindow] Regenerate translate error:', error);
  } finally {
    setRegeneratingId(null);
  }
};

  const handleRegenerateGenerate = async (result) => {
  if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.GENERATE) <= 0) return;
  
  setRegeneratingId(result.id);
  
  try {
    const originalMessages = result.originalMessages || [];
    
    if (originalMessages.length === 0) {
      console.warn('[ChatWindow] No original messages found for regeneration');
      return;
    }

    await aiWorkspace.regenerateResult(
      result.id, 
      ACTION_TYPES.GENERATE,
      async (params) => {
        const results = await aiAgentService.processAction({
          ...params,
          messages: originalMessages,
          context: {
            chatId: chat?.id,
            chatName: getChatName(),
          },
        });
        
        return results.map(r => ({
          ...r,
          originalMessages: originalMessages,
        }));
      },
      { originalMessages }
    );
  } catch (error) {
    // 🆕 ADICIONAR
    if (handleAgentError(error)) return;
    console.error('[ChatWindow] Regenerate generate error:', error);
  } finally {
    setRegeneratingId(null);
  }
};

  const handleExecuteCustom = async (prompt) => {
  if (!prompt?.trim()) return;

  const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
  const originalPrompt = prompt.trim();
  
  try {
    await aiWorkspace.executeCustom(originalPrompt, async ({ action, prompt: userPrompt }) => {
      const results = await aiAgentService.processAction({
        action,
        prompt: userPrompt,
        context: {
          userId: userMetadata.id || 'anonymous',
          chatId: chat?.id,
          chatName: getChatName(),
        },
      });
      
      return results.map(r => ({
        ...r,
        originalPrompt: originalPrompt,
      }));
    }, {
      userId: userMetadata.id || 'anonymous',
      chatId: chat?.id,
      chatName: getChatName(),
    });
  } catch (error) {
    // 🆕 ADICIONAR: Verificar se é erro de agente não configurado
    if (handleAgentError(error)) return;
    
    console.error('[ChatWindow] Custom prompt error:', error);
  }
};


  const handleRegenerateCustom = async (result) => {
  if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.CUSTOM) <= 0) return;
  
  setRegeneratingId(result.id);
  
  try {
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const originalPrompt = result.originalPrompt || '';
    
    if (!originalPrompt) {
      console.warn('[ChatWindow] No original prompt found for regeneration');
      return;
    }
    
    await aiWorkspace.regenerateResult(
      result.id, 
      ACTION_TYPES.CUSTOM,
      async (params) => {
        const results = await aiAgentService.processAction({
          ...params,
          prompt: originalPrompt,
          context: {
            userId: userMetadata.id || 'anonymous',
            chatId: chat?.id,
            chatName: getChatName(),
          },
        });
        
        return results.map(r => ({
          ...r,
          originalPrompt: originalPrompt,
        }));
      },
      { originalPrompt }
    );
  } catch (error) {
    // 🆕 ADICIONAR
    if (handleAgentError(error)) return;
    console.error('[ChatWindow] Regenerate custom error:', error);
  } finally {
    setRegeneratingId(null);
  }
};

  const handleToggleMessageSelection = (msgId, msg) => {
    const isBlocked = isMessageBlocked(msg);
    const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
    const isCurrentlySelected = aiWorkspace.selectedMessages.has(msgId);
    
    if (isCurrentlySelected) {
      aiWorkspace.toggleMessageSelection(msgId, msg);
      return;
    }
    
    if (isBlocked || isAtLimit) {
      return;
    }
    
    aiWorkspace.toggleMessageSelection(msgId, msg);
  };

  // Group messages by date (maintaining chronological order)
  const groupedMessages = useMemo(() => {
    const groups = {};
    const dateOrder = []; // Track order of dates as they appear
    
    messages.forEach(msg => {
      const dateKey = formatDate(msg.timestamp || msg.created_at);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
        dateOrder.push(dateKey);
      }
      groups[dateKey].push(msg);
    });
    
    // Return as ordered entries to maintain chronological order
    const orderedGroups = {};
    dateOrder.forEach(key => {
      orderedGroups[key] = groups[key];
    });
    
    return orderedGroups;
  }, [messages]);

  // ============================================
  // STYLES (Padronizado com WhatsApp Pessoal - cores verdes)
  // ============================================

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative',
    },
    emptyContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgSecondary,
    },
    emptyContent: {
      textAlign: 'center',
      color: colors.textSecondary,
    },
    emptyTitle: {
      fontSize: '32px',
      fontWeight: 300,
      color: colors.textPrimary,
      marginBottom: '12px',
    },
    emptyText: {
      fontSize: '14px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 16px',
      backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`,
      minHeight: '60px',
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      overflow: 'hidden',
    },
    avatarImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    avatarPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    headerDetails: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerName: {
      fontWeight: 500,
      fontSize: '16px',
      color: colors.textPrimary,
    },
    headerStatus: {
      fontSize: '13px',
      color: colors.textSecondary,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    headerActionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgChat,
    },
    messagesLoading: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    messagesEmpty: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    messageGroup: {
      marginBottom: '16px',
    },
    dateDivider: {
      display: 'flex',
      justifyContent: 'center',
      margin: '16px 0',
    },
    dateDividerText: {
      backgroundColor: colors.bgTertiary,
      padding: '6px 14px',
      borderRadius: '8px',
      fontSize: '12px',
      color: colors.textSecondary,
      boxShadow: colors.shadowSm,
    },
    messageRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginBottom: '2px',
      padding: '2px 0',
      borderRadius: '8px',
      transition: 'background-color 0.15s ease',
    },
    messageRowSelected: {
      backgroundColor: `${workspaceColors.accentPrimary}15`,
      margin: '2px -8px',
      padding: '2px 8px',
    },
    messageBubbleWrapper: {
      flex: 1,
      display: 'flex',
    },
    messageSent: {
      justifyContent: 'flex-end',
    },
    messageReceived: {
      justifyContent: 'flex-start',
    },
    messageContentSent: {
      maxWidth: '65%',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: colors.messageSent,
      color: colors.messageSentText,
      borderTopRightRadius: '2px',
    },
    messageContentReceived: {
      maxWidth: '65%',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: colors.messageReceived,
      color: colors.messageReceivedText,
      borderTopLeftRadius: '2px',
    },
    messageFailed: {
      backgroundColor: `${colors.danger || '#ef4444'}20`,
      border: `1px solid ${colors.danger || '#ef4444'}40`,
    },
    messageText: {
      fontSize: '14px',
      lineHeight: 1.4,
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      margin: 0,
    },
    messageMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '4px',
      marginTop: '4px',
    },
    messageTimeSent: {
      fontSize: '11px',
      color: colors.messageTimeSent,
    },
    messageTimeReceived: {
      fontSize: '11px',
      color: colors.textTertiary,
    },
    messageMedia: {
      maxWidth: '300px',
      borderRadius: '8px',
      marginBottom: '4px',
    },
    scrollButton: {
      position: 'absolute',
      bottom: '80px',
      right: '24px',
      width: '42px',
      height: '42px',
      borderRadius: '50%',
      backgroundColor: colors.bgPrimary,
      border: 'none',
      boxShadow: colors.shadowMd,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
      zIndex: 10,
    },
    windowError: {
      backgroundColor: `${colors.warning || '#f59e0b'}15`,
      borderTop: `1px solid ${colors.warning || '#f59e0b'}30`,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    windowErrorText: {
      flex: 1,
      fontSize: '13px',
      color: colors.warning || '#f59e0b',
    },
    inputContainer: {
      backgroundColor: colors.headerBg,
    },
    inputForm: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '8px',
      padding: '12px 16px',
    },
    inputActionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '42px',
      height: '42px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
    },
    inputWrapper: {
      flex: 1,
      backgroundColor: colors.inputBg,
      borderRadius: '8px',
      border: `1px solid ${colors.inputBorder}`,
    },
    textarea: {
      width: '100%',
      border: 'none',
      background: 'transparent',
      padding: '12px 16px',
      fontSize: '15px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      maxHeight: '100px',
      lineHeight: 1.4,
      fontFamily: 'inherit',
    },
    sendBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '42px',
      height: '42px',
      border: 'none',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
    },
    sendBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  // ============================================
  // RENDER
  // ============================================

  if (!chat) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyContent}>
          <h3 style={styles.emptyTitle}>MinistrareIA</h3>
          <p style={styles.emptyText}>Selecione uma conversa para começar a enviar mensagens</p>
        </div>
      </div>
    );
  }

  const canSend = (newMessage.trim() || attachedFiles.length > 0) && !uploading;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerInfo}>
          <div style={styles.headerAvatar}>{getChatAvatar()}</div>
          <div style={styles.headerDetails}>
            <span style={styles.headerName}>{getChatName()}</span>
            <span style={styles.headerStatus}>
              {chat.contact_name ? `+${chat.contact_phone}` : ''}
            </span>
          </div>
        </div>
        <div style={styles.headerActions}>
          {/* Autopilot activate / block buttons */}
          {(() => {
            const chatPhone = chat.contact_phone;
            const isAutopilotOn = (autopilotContacts || []).includes(chatPhone);
            const isBlocked = (blockedContacts || []).some(c =>
              c.replace(/\D/g, '') === chatPhone?.replace(/\D/g, '')
            );
            return (
              <>
                {/* Activate / Deactivate agent button */}
                <button
                  onClick={() => onToggleAutopilot?.(chatPhone, !isAutopilotOn)}
                  disabled={isBlocked}
                  style={{
                    ...styles.headerActionBtn,
                    backgroundColor: isAutopilotOn ? `${colors.accentPrimary}15` : 'transparent',
                    border: `1.5px solid ${isAutopilotOn ? colors.accentPrimary : colors.borderColor}`,
                    borderRadius: '20px',
                    width: 'auto',
                    padding: '5px 10px',
                    gap: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: isBlocked ? 0.4 : 1,
                    cursor: isBlocked ? 'not-allowed' : 'pointer',
                  }}
                  title={isBlocked ? 'Desbloqueie o contato primeiro' : isAutopilotOn ? 'Desativar agente para este contato' : 'Ativar agente para este contato'}
                >
                  <Bot size={14} color={isAutopilotOn ? colors.accentPrimary : colors.textSecondary} />
                  <span style={{ fontSize: '11px', fontWeight: 500, color: isAutopilotOn ? colors.accentPrimary : colors.textSecondary }}>
                    {isAutopilotOn ? 'Ativo' : 'Ativar'}
                  </span>
                </button>
                {/* Block / Unblock agent button */}
                <button
                  onClick={() => onToggleBlockedContact?.(chatPhone, !isBlocked)}
                  style={{
                    ...styles.headerActionBtn,
                    backgroundColor: isBlocked ? '#EF444415' : 'transparent',
                    border: `1.5px solid ${isBlocked ? '#EF4444' : colors.borderColor}`,
                    borderRadius: '20px',
                    width: 'auto',
                    padding: '5px 10px',
                    gap: '5px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  title={isBlocked ? 'Desbloquear agente para este contato' : 'Bloquear agente para este contato'}
                >
                  <ShieldOff size={14} color={isBlocked ? '#EF4444' : colors.textSecondary} />
                  <span style={{ fontSize: '11px', fontWeight: 500, color: isBlocked ? '#EF4444' : colors.textSecondary }}>
                    {isBlocked ? 'Bloqueado' : 'Bloquear'}
                  </span>
                </button>
              </>
            );
          })()}
          <button style={styles.headerActionBtn} title="Mais opções">
            <MoreVertical size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        style={styles.messagesContainer}
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {loading ? (
          <div style={styles.messagesLoading}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
            <span>Carregando mensagens...</span>
          </div>
        ) : messages.length === 0 ? (
          <div style={styles.messagesEmpty}>
            <p style={{ fontSize: '16px', marginBottom: '4px' }}>Nenhuma mensagem ainda</p>
            <span style={{ fontSize: '14px', color: colors.textTertiary }}>
              Envie uma mensagem para iniciar a conversa
            </span>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date} style={styles.messageGroup}>
              <div style={styles.dateDivider}>
                <span style={styles.dateDividerText}>{date}</span>
              </div>
              {msgs.map((msg, index) => {
                const isSent = msg.from_me;
                const msgId = msg.id || msg.wamid || `msg-${index}`;
                const isSelected = aiWorkspace.selectedMessages.has(msgId);
                const isHovered = hoveredMessage === msgId;
                const showCheckbox = isHovered || isSelected || aiWorkspace.selectedMessages.size > 0;
                const msgIsBlocked = isMessageBlocked(msg);
                const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
                const isFailed = msg.status === 'failed';

                return (
                  <div
                    key={msgId}
                    style={{
                      ...styles.messageRow,
                      ...(isSelected ? styles.messageRowSelected : {}),
                    }}
                    onMouseEnter={() => setHoveredMessage(msgId)}
                    onMouseLeave={() => setHoveredMessage(null)}
                  >
                    {/* Checkbox for selection */}
                    <MessageCheckbox
                      isSelected={isSelected}
                      isVisible={showCheckbox}
                      onToggle={() => handleToggleMessageSelection(msgId, msg)}
                      isBlocked={msgIsBlocked}
                      isAtLimit={isAtLimit && !isSelected}
                      blockReason={msgIsBlocked ? 'Apenas mensagens de texto' : ''}
                    />

                    {/* Message bubble */}
                    <div 
                      style={{
                        ...styles.messageBubbleWrapper,
                        ...(isSent ? styles.messageSent : styles.messageReceived),
                      }}
                    >
                      <div style={{
                        ...(isSent ? styles.messageContentSent : styles.messageContentReceived),
                        ...(isFailed ? styles.messageFailed : {}),
                      }}>
                        {/* Media Content */}
                        {hasMediaContent(msg) && (
                          <div style={{ marginBottom: msg.text ? '8px' : '0' }}>
                            <WABusinessMessageMedia msg={msg} isSent={isSent} />
                          </div>
                        )}
                        
                        {/* Text Content (skip for image/video as the media component shows caption) */}
                        {msg.text && !(hasMediaContent(msg) && (msg.type === 'image' || msg.type === 'video')) && (
                          <p style={styles.messageText}>{msg.text}</p>
                        )}
                        
                        {/* Failed message error */}
                        {isFailed && msg.error_message && (
                          <p style={{ 
                            fontSize: '11px', 
                            color: colors.danger || '#ef4444',
                            marginTop: '4px',
                            fontStyle: 'italic'
                          }}>
                            {msg.error_message}
                          </p>
                        )}
                        
                        <div style={styles.messageMeta}>
                          <span style={isSent ? styles.messageTimeSent : styles.messageTimeReceived}>
                            {formatTime(msg.timestamp || msg.created_at)}
                          </span>
                          {isSent && getMessageStatusIcon(msg.status)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll Button */}
      {showScrollButton && (
        <button style={styles.scrollButton} onClick={scrollToBottom}>
          <ArrowDown size={20} />
        </button>
      )}

      {/* AI Workspace */}
      <AIWorkspace
        isOpen={aiWorkspace.isWorkspaceOpen}
        onToggleWorkspace={aiWorkspace.toggleWorkspace}
        selectedCount={aiWorkspace.selectedMessages.size}
        inputText={newMessage}
        
        // Dropdown
        activeDropdown={aiWorkspace.activeDropdown}
        onToggleDropdown={aiWorkspace.toggleDropdown}
        onCloseDropdown={aiWorkspace.closeDropdown}
        
        // Translate
        translateLanguages={aiWorkspace.translateLanguages}
        onToggleTranslateLanguage={aiWorkspace.toggleTranslateLanguage}
        translateResults={aiWorkspace.translateResults}
        translateProcessing={aiWorkspace.translateProcessing}
        translateError={aiWorkspace.translateError}
        hasTranslateResults={aiWorkspace.hasTranslateResults}
        
        // Generate
        generateResults={aiWorkspace.generateResults}
        generateProcessing={aiWorkspace.generateProcessing}
        generateError={aiWorkspace.generateError}
        hasGenerateResults={aiWorkspace.hasGenerateResults}
        
        // Custom Prompt
        customResults={aiWorkspace.customResults}
        customProcessing={aiWorkspace.customProcessing}
        customError={aiWorkspace.customError}
        hasCustomResults={aiWorkspace.hasCustomResults}
        
        // History
        historyItems={aiWorkspace.historyItems}
        isHistoryOpen={aiWorkspace.isHistoryOpen}
        onToggleHistory={aiWorkspace.toggleHistory}
        onRemoveFromHistory={aiWorkspace.removeFromHistory}
        onClearHistory={aiWorkspace.clearHistory}
        hasHistory={aiWorkspace.hasHistory}
        
        // Actions
        onClear={aiWorkspace.clearSelection}
        onExecuteTranslate={handleExecuteTranslate}
        onExecuteGenerate={handleExecuteGenerate}
        onExecuteCustom={handleExecuteCustom}
        onUseResponse={handleUseResponse}
        onRegenerateTranslate={handleRegenerateTranslate}
        onRegenerateGenerate={handleRegenerateGenerate}
        onRegenerateCustom={handleRegenerateCustom}
        onRemoveTranslateResult={aiWorkspace.removeTranslateResult}
        onRemoveGenerateResult={aiWorkspace.removeGenerateResult}
        onRemoveCustomResult={aiWorkspace.removeCustomResult}
        
        // Regeneration
        getTranslateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.TRANSLATE)}
        getGenerateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.GENERATE)}
        getCustomRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.CUSTOM)}
        regeneratingId={regeneratingId}
      />

      {/* 24h Window Error */}
      {windowError && (
        <div style={styles.windowError}>
          <AlertCircle size={20} color={colors.warning || '#f59e0b'} />
          <span style={styles.windowErrorText}>{windowError}</span>
          <button 
            style={{ 
              border: 'none', 
              background: 'transparent', 
              color: colors.textTertiary, 
              cursor: 'pointer',
              padding: '4px',
            }}
            onClick={() => setWindowError(null)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Input Area */}
      <div style={styles.inputContainer}>
        {/* Upload Error Message */}
        {uploadError && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: `${colors.danger || '#ef4444'}15`,
            borderTop: `1px solid ${colors.danger || '#ef4444'}30`,
            color: colors.danger || '#ef4444',
            fontSize: '12px',
          }}>
            ⚠️ {uploadError}
          </div>
        )}
        
        {/* Attached Files Preview */}
        <AttachedFilesPreview
          files={attachedFiles}
          onRemoveFile={handleRemoveAttachedFile}
          onClearAll={handleClearAttachedFiles}
        />
        
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={fileUploadService.getAcceptString()}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {/* Input Form */}
        <form onSubmit={handleSend} style={styles.inputForm}>
          <button 
            type="button" 
            style={{
              ...styles.inputActionBtn,
              ...(uploading ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
            }}
            title={uploading ? 'Enviando arquivos...' : 'Anexar arquivo'}
            onClick={handleAttachClick}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Paperclip size={24} />
            )}
          </button>
          <div style={styles.inputWrapper}>
            <textarea
              ref={inputRef}
              placeholder="Digite uma mensagem"
              value={newMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              rows={1}
              disabled={sending}
              style={styles.textarea}
            />
          </div>
          <button
            type="submit"
            style={{
              ...styles.sendBtn,
              ...(!canSend || sending ? styles.sendBtnDisabled : {}),
            }}
            disabled={!canSend || sending}
          >
            {sending ? (
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Send size={20} />
            )}
          </button>
        </form>
      </div>

      {/* 🆕 ADICIONAR: Modal de criar agente */}
      <CreateAgentModal />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ChatWindow;